/**
 * The Velora desktop app's bridge (`window.veloraDesktop`, apps/desktop
 * preload), as the web app sees it. Absent in a plain browser. Every member
 * is optional: an older desktop build lacks the newer ones, and the web app
 * must work either way.
 *
 * Only ids, paths, counts and the practice's office names cross the bridge,
 * never patient names or numbers: the desktop main process does not handle
 * PHI.
 */

/** An office the person can pick (id and display name only). */
export interface DesktopOffice {
  id: string;
  name: string;
}
export interface DesktopBridge {
  isDesktop?: boolean;
  version?: string;
  /** Open a workspace path in the main window and bring it forward. */
  openInMain?: (path: string) => void;
  /** Main window: take in-app navigation requests (deep links, the call pop). */
  onNavigateRequest?: (listener: (path: string) => void) => () => void;
  callPop?: {
    /** The pop window has calls to show (it shows itself only when needed). */
    setActive: (active: { calls: number; ringing: boolean }) => void;
    /**
     * The office selected in Velora (`all` or an office id) and the offices
     * the person can pick: the desktop pop listens for the same office and
     * shows it, and its tray can switch it (0.4.1+).
     */
    setOffice?: (office: { scope: string; offices: DesktopOffice[] }) => void;
    /** The tray picked an office: switch the selector to it (`all` or an id). */
    onOfficeRequest?: (listener: (scope: string) => void) => () => void;
  };
  /**
   * The Velora Capture Helper ships inside the app (Windows installs): the
   * app installs or repairs it for this Windows user. The web app keeps
   * talking to the helper on 127.0.0.1:17470 as in a browser.
   */
  captureHelper?: { bundled: boolean; install: () => void };
  /** `overlay`: no system title bar; the top bar is the window's drag handle. */
  titleBar?: 'overlay' | 'native';
}

export function desktopBridge(): DesktopBridge | null {
  if (typeof window === 'undefined') return null;
  const b = (window as { veloraDesktop?: DesktopBridge }).veloraDesktop;
  return b?.isDesktop ? b : null;
}
